// Design tokens for Merchant Setup.
//
// Why this file exists: `src/app/pages/merchants` carried 359 hardcoded hex
// literals across 120 distinct values (including a raw #0000FF), so no two
// screens in the module looked related. The values below are not invented --
// they are the slate + indigo ramp the newer merchant screens (KYC Analytics,
// the pricing/TAT wizards, the KYC drill-downs) had already converged on,
// promoted to one place so the rest of the module can be moved onto it.
//
// Convention follows AppointmentDeliveriesV2/shared/tokens.js, which is the
// only other module in the app with a real token file.

export const C = {
    // ── Ink / neutrals (slate, warmed very slightly toward the indigo accent) ──
    ink: '#0f172a',
    inkStrong: '#1e293b',
    inkBody: '#334155',
    inkMuted: '#475569',
    inkFaint: '#64748b',
    inkGhost: '#94a3b8',

    // ── Surfaces ──
    page: '#f8fafc',
    surface: '#ffffff',
    surfaceAlt: '#f1f5f9',
    surfaceSunken: '#f8fafc',

    // ── Lines ──
    line: '#e2e8f0',
    lineStrong: '#cbd5e1',
    lineSoft: '#eef2f7',

    // ── Accent (indigo/violet -- the module's existing signature) ──
    accent: '#4338ca',
    accentHover: '#3730a3',
    accentSoft: '#eef2ff',
    accentBorder: '#c7d2fe',
    accentInk: '#1e40af',

    // ── Semantic state. Separate from the accent on purpose: these encode
    //    truth about a merchant's config, and must never be used decoratively.
    ok: '#15803d',
    okSoft: '#dcfce7',
    okBorder: '#bbf7d0',

    warn: '#b45309',
    warnSoft: '#fef3c7',
    warnBorder: '#fde68a',

    danger: '#b91c1c',
    dangerSoft: '#fef2f2',
    dangerBorder: '#fecaca',

    info: '#0369a1',
    infoSoft: '#e0f2fe',
    infoBorder: '#bae6fd',

    neutral: '#64748b',
    neutralSoft: '#f1f5f9',
    neutralBorder: '#e2e8f0',
};

// ── Elevation. Soft and low -- this is a config surface, not a dashboard. ──
export const SHADOW = {
    none: 'none',
    card: '0 1px 2px rgba(15,23,42,0.04), 0 1px 3px rgba(15,23,42,0.06)',
    raised: '0 2px 4px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.12)',
    pop: '0 8px 32px -8px rgba(15,23,42,0.18)',
};

export const RADIUS = { sm: '6px', md: '10px', lg: '14px', pill: '999px' };

// ── One type scale for the whole module. ──
export const TYPE = {
    pageTitle:  { fontSize: 20,   fontWeight: 700, letterSpacing: '-0.02em', color: C.ink },
    sectionTitle:{ fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.01em', color: C.ink },
    rowLabel:   { fontSize: 13.5, fontWeight: 600, color: C.inkStrong },
    rowHelp:    { fontSize: 12.5, fontWeight: 400, color: C.inkFaint, lineHeight: 1.5 },
    value:      { fontSize: 13.5, fontWeight: 600, color: C.inkBody },
    meta:       { fontSize: 11,   fontWeight: 500, color: C.inkGhost },
    eyebrow:    { fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.inkGhost },
};

// ── Config state. The single most useful thing a config screen can show is
//    whether a setting has actually been set -- previously indistinguishable
//    from "set to the default". ──
export const CONFIG_STATE = {
    set:       { label: 'Configured',  fg: C.ok,      bg: C.okSoft,      border: C.okBorder },
    unset:     { label: 'Not set',     fg: C.neutral, bg: C.neutralSoft, border: C.neutralBorder },
    inherited: { label: 'Default',     fg: C.info,    bg: C.infoSoft,    border: C.infoBorder },
    attention: { label: 'Needs review',fg: C.warn,    bg: C.warnSoft,    border: C.warnBorder },
    inactive:  { label: 'Overridden',  fg: C.danger,  bg: C.dangerSoft,  border: C.dangerBorder },
};

// ── Readiness, for the merchant list. Maps onto fields the merchant document
//    already carries (kycStatus/status, tatStatus, rateCardVersion,
//    allocationVersion, commercialConstructConfirmed). ──
export const READINESS = {
    done:    { fg: C.ok,      bg: C.okSoft,      border: C.okBorder },
    pending: { fg: C.warn,    bg: C.warnSoft,    border: C.warnBorder },
    blocked: { fg: C.danger,  bg: C.dangerSoft,  border: C.dangerBorder },
    none:    { fg: C.neutral, bg: C.neutralSoft, border: C.neutralBorder },
};

// ── Reusable sx fragments, so cards/rows don't re-derive the same styles. ──
export const SX = {
    card: {
        bgcolor: C.surface,
        border: `1px solid ${C.line}`,
        borderRadius: RADIUS.md,
        boxShadow: SHADOW.card,
    },
    cardFlush: {
        bgcolor: C.surface,
        border: `1px solid ${C.line}`,
        borderRadius: RADIUS.md,
        boxShadow: SHADOW.none,
    },
    pill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.85,
        py: '2px',
        borderRadius: RADIUS.pill,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.02em',
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
    },
    // Primary action. One button style for the whole module.
    cta: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 13,
        borderRadius: RADIUS.sm,
        boxShadow: 'none',
        bgcolor: C.accent,
        '&:hover': { bgcolor: C.accentHover, boxShadow: 'none' },
    },
    ctaGhost: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 13,
        borderRadius: RADIUS.sm,
        color: C.inkMuted,
        borderColor: C.lineStrong,
        '&:hover': { borderColor: C.accent, color: C.accent, bgcolor: C.accentSoft },
    },
};
